import { useTierFeatures } from "@/hooks/useTierFeatures";
import { TierLockedCard } from "@/components/TierLockedCard";
import { toast } from "@/lib/toast";
import { isNative } from "@/lib/capacitor";
import { shareReportBlob } from "@/lib/nativeShare";

interface Props {
  /** Title used in the file name + PDF heading. */
  title: string;
  /** Plain-text fields rendered as a key/value table in the report. */
  fields: Array<{ label: string; value: string | number | null | undefined }>;
  /** Optional cover photo URL (must be same-origin or CORS-enabled). */
  photo?: string | null;
  /** Optional verdict / notes paragraph. */
  notes?: string | null;
}

/**
 * "Export PDF" action for a scan. Generates a print-friendly HTML window
 * and opens the browser's native print-to-PDF dialog — no extra libraries.
 * White-label (no FIG branding) is unlocked for Pro Seller.
 */
export function ScanPdfExportButton({ title, fields, photo, notes }: Props) {
  const { features } = useTierFeatures();

  if (!features.canExportPdf) {
    return (
      <TierLockedCard
        feature="PDF export"
        requiredTier="Seller"
        blurb="Download a polished, printable authentication report for any scan."
      />
    );
  }

  const buildHtml = ({ forPrint = false }: { forPrint?: boolean } = {}): string => {
    const branded = !features.canWhiteLabelPdf;
    const safe = (s: string) =>
      s.replace(/[&<>"']/g, (c) =>
        ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] as string),
      );
    const rows = fields
      .filter((f) => f.value != null && String(f.value).trim() !== "")
      .map(
        (f) => `
          <tr>
            <th style="text-align:left;padding:6px 10px;border-bottom:1px solid #ddd;font-family:monospace;font-size:11px;text-transform:uppercase;letter-spacing:0.08em;color:#666;">${safe(f.label)}</th>
            <td style="padding:6px 10px;border-bottom:1px solid #ddd;font-family:'Georgia',serif;font-size:14px;color:#222;">${safe(String(f.value))}</td>
          </tr>`,
      )
      .join("");
    return `<!doctype html>
<html><head><meta charset="utf-8"/>
<title>${safe(title)} — Authentication Report</title>
<style>
  body { font-family: Georgia, serif; color:#222; padding:40px; max-width:720px; margin:0 auto; }
  h1 { font-size:28px; margin:0 0 4px; }
  .eyebrow { font-family:monospace; font-size:11px; letter-spacing:0.12em; text-transform:uppercase; color:#888; }
  .photo { width:100%; max-height:340px; object-fit:cover; margin:18px 0; border:1px solid #ccc; }
  table { width:100%; border-collapse:collapse; margin-top:16px; }
  .notes { margin-top:18px; font-style:italic; color:#444; line-height:1.5; }
  .footer { margin-top:32px; padding-top:14px; border-top:1px solid #ddd; font-family:monospace; font-size:10px; color:#888; letter-spacing:0.08em; text-transform:uppercase; }
</style></head>
<body>
  <div class="eyebrow">FIG Verify · Authentication Report</div>
  <h1>${safe(title)}</h1>
  ${photo ? `<img class="photo" src="${safe(photo)}" alt=""/>` : ""}
  <table>${rows}</table>
  ${notes ? `<div class="notes">${safe(notes)}</div>` : ""}
  ${branded ? `<div class="footer">Generated by FIG Verify · ${new Date().toLocaleDateString()}</div>` : `<div class="footer">${new Date().toLocaleDateString()}</div>`}
  ${forPrint ? `<script>window.onload = () => { setTimeout(() => window.print(), 250); };</script>` : ""}
</body></html>`;
  };

  const handleExport = async () => {
    if (isNative) {
      const html = buildHtml();
      const slug = title.replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 40).replace(/^-+|-+$/g, "") || "report";
      const filename = `fig-cert-${slug}.html`;
      const blob = new Blob([html], { type: "text/html" });
      try {
        await shareReportBlob(blob, filename);
      } catch (err: any) {
        toast.error(err?.message ?? "Couldn't share report");
      }
      return;
    }

    const html = buildHtml({ forPrint: true });
    const win = window.open("", "_blank", "width=820,height=1000");
    if (!win) {
      toast.error("Pop-up blocked — allow pop-ups to export PDFs.");
      return;
    }
    win.document.write(html);
    win.document.close();
  };

  return (
    <button
      type="button"
      onClick={handleExport}
      style={{
        width: "100%",
        background: "hsl(var(--cream))",
        color: "hsl(var(--ink))",
        border: "1px solid hsl(var(--ink))",
        padding: "10px 12px",
        cursor: "pointer",
        fontFamily: "'IBM Plex Mono', monospace",
        fontSize: 10,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
      }}
    >
      Export PDF report{features.canWhiteLabelPdf ? " (white-label)" : ""}
    </button>
  );
}